import { collection, addDoc, getDocs, doc, getDoc, updateDoc, deleteDoc, serverTimestamp, setDoc, runTransaction } from 'firebase/firestore';
import { db } from '../../sdk_initialization';

const COLLECTION = 'events';
const COUNTERS_COLLECTION = 'counters';
const EVENT_ID_COUNTER_DOC = 'eventId';

const parseRequiredPositiveMinutes = (value, fieldName) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(`${fieldName} must be a positive number of minutes`);
  }
  return parsed;
};

/**
 * Generate next event ID using counter
 * @returns {Promise<string>} - 6-digit event ID (e.g., '100001')
 */
const generateNextEventId = async () => {
  const counterRef = doc(db, COUNTERS_COLLECTION, EVENT_ID_COUNTER_DOC);
  
  return await runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);
    
    let nextValue = 100001; // Start at 100001 for 6-digit event IDs
    
    if (counterDoc.exists()) {
      const currentValue = counterDoc.data().value || 100000;
      nextValue = currentValue + 1;
    }
    
    transaction.set(counterRef, { value: nextValue, updatedAt: serverTimestamp() }, { merge: true });
    
    return String(nextValue).padStart(6, '0');
  });
};

export const addEvent = async (data) => {
  // Generate unique 6-digit event ID
  const eventId = await generateNextEventId();
  const shoppingCartWait = parseRequiredPositiveMinutes(data.shoppingCartWait, 'shoppingCartWait');
  const paymentWait = parseRequiredPositiveMinutes(data.paymentWait, 'paymentWait');
  
  const payload = {
    salesforceEventId: data.salesforceEventId || '',
    salesforceBazaarId: data.salesforceBazaarId || '',
    name: data.name || '',
    status: data.status || 'Draft',
    statusMessage: data.statusMessage || '',
    description: data.description || '',
    startDate: data.startDate || '',
    endDate: data.endDate || '',
    location: data.location || '',
    address: data.address || '',
    linkToEventSite: data.linkToEventSite || '',
    cancellationPolicy: data.cancellationPolicy || '',
    termsAndConditions: data.termsAndConditions || '',
    policyLink: data.policyLink || '',
    rejectionMessage: data.rejectionMessage || '',
    eventAdminContactEmail: data.eventAdminContactEmail || '',
    badgeTemplateName: data.badgeTemplateName || '',
    packagedFoodVendorMessage: data.packagedFoodVendorMessage || '',
    packagedFoodVendorMessageDisplay: Boolean(data.packagedFoodVendorMessageDisplay ?? false),
    applicationAckMessage: data.applicationAckMessage || '',
    checkinSchedule: Array.isArray(data.checkinSchedule) ? data.checkinSchedule : [],
    requiredItems: Array.isArray(data.requiredItems) ? data.requiredItems : [],
    consentMessage: data.consentMessage || '',
    consentFontSize: data.consentFontSize || '14px',
    approvalEmailText: data.approvalEmailText || '',
    refundPercentage: Number(data.refundPercentage ?? 0),
    refundAmount: Number(data.refundAmount ?? 0),
    applyRefundFeePerBooth: Boolean(data.applyRefundFeePerBooth ?? false),
    layoutId: data.layoutId || '',
    eventAdmins: Array.isArray(data.eventAdmins) ? data.eventAdmins : [],
    organizers: Array.isArray(data.organizers) ? data.organizers : [],
  freeAttendeesPerBooth: Number(data.freeAttendeesPerBooth ?? 3),
  additionalAttendeePrice: Number(data.additionalAttendeePrice ?? 0), // Already in cents from Events.jsx
    shoppingCartWait,
    paymentWait,
    allowApplications: Boolean(data.allowApplications ?? true),
    allowRegistration: Boolean(data.allowRegistration ?? true),
    bazaarPaymentInfo: data.bazaarPaymentInfo || { provider: '', baseUrl: '', clientId: '', secretId: '', paymentConfigId: '' },
    createdByUid: data.createdByUid || data.userId || null,
    updatedByUid: data.updatedByUid || data.userId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  
  // Use setDoc with the generated event ID instead of addDoc
  const ref = doc(db, COLLECTION, eventId);
  await setDoc(ref, payload);
  const snap = await getDoc(ref);
  return { id: eventId, ...(snap.exists() ? snap.data() : payload) };
};

export const listEvents = async () => {
  const col = collection(db, COLLECTION);
  const snap = await getDocs(col);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getEvent = async (id) => {
  const ref = doc(db, COLLECTION, id);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const updateEvent = async (id, data) => {
  const ref = doc(db, COLLECTION, id);
  const shoppingCartWait = parseRequiredPositiveMinutes(data.shoppingCartWait, 'shoppingCartWait');
  const paymentWait = parseRequiredPositiveMinutes(data.paymentWait, 'paymentWait');
  const updatePayload = {
    ...data,
  freeAttendeesPerBooth: Number(data.freeAttendeesPerBooth ?? 3),
  additionalAttendeePrice: Number(data.additionalAttendeePrice ?? 0), // Already in cents from Events.jsx
    shoppingCartWait,
    paymentWait,
    packagedFoodVendorMessageDisplay: Boolean(data.packagedFoodVendorMessageDisplay ?? false),
    refundPercentage: Number(data.refundPercentage ?? 0),
    refundAmount: Number(data.refundAmount ?? 0),
    applyRefundFeePerBooth: Boolean(data.applyRefundFeePerBooth ?? false),
    allowApplications: Boolean(data.allowApplications ?? true),
    allowRegistration: Boolean(data.allowRegistration ?? true),
    checkinSchedule: Array.isArray(data.checkinSchedule) ? data.checkinSchedule : [],
    requiredItems: Array.isArray(data.requiredItems) ? data.requiredItems : [],
    consentMessage: data.consentMessage || '',
    consentFontSize: data.consentFontSize || '14px',
    approvalEmailText: data.approvalEmailText || '',
    bazaarPaymentInfo: data.bazaarPaymentInfo || { provider: '', baseUrl: '', clientId: '', secretId: '', paymentConfigId: '' },
    updatedByUid: data.updatedByUid || data.userId || null,
    updatedAt: serverTimestamp() 
  };
  // Normalize eventAdmins when provided
  if (data.eventAdmins !== undefined) {
    updatePayload.eventAdmins = Array.isArray(data.eventAdmins) ? data.eventAdmins : [];
  }
  // Normalize organizers when provided
  if (data.organizers !== undefined) {
    updatePayload.organizers = Array.isArray(data.organizers) ? data.organizers : [];
  }
  await updateDoc(ref, updatePayload);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : { id, ...data };
};

export const deleteEvent = async (id) => {
  const ref = doc(db, COLLECTION, id);
  await deleteDoc(ref);
  return true;
};
